import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';
import reportWebVitals from './reportWebVitals';
import RootErrorBoundary from './components/RootErrorBoundary';
import { captureFirstTouch } from './utils/firstTouch';
import { captureBlogVoiceHire } from './utils/blogVoiceCapture';
import { initFeErrorReporting } from './utils/feErrorReporting';
import { initViewportHeightFix } from './utils/viewportHeight';
import { installWebUpdateOnResume } from './utils/webUpdateOnResume';

// Polyfill for react-snap's prerender browser (CEL-672): its bundled Chromium predates
// Object.hasOwn (Chrome 93). The support pages' markdown pipeline uses it; without this
// the prerender crashes and the ENTIRE build falls back to no prerendered routes.
if (!Object.hasOwn) {
  Object.hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
}

// Google-Translate / extension DOM guard (FE error class, 2026-08-26 triage): browser
// extensions — Chrome auto-translate above all — mutate text nodes under React (wrapping
// them in <font> tags), so React's commit-phase removeChild/insertBefore later targets a
// DOM it no longer owns and throws NotFoundError, tripping the root error boundary — a
// translated visitor saw our error screen on marketing pages (35 errs/14d, mostly /,
// /welcome, /pricing). The standard mitigation: make the two calls no-op gracefully when
// the relationship no longer holds; React recovers on its next render instead of crashing.
// Guarded to browser environments only (react-snap's prerenderer keeps native behavior:
// window.snapSaveState is defined there before scripts run — and regardless, no extension
// mutates the prerender DOM, so the patch is inert for it).
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChildGuard(child, ...rest) {
    if (child && child.parentNode !== this) {
      // eslint-disable-next-line no-console
      console.warn('[cc-dom-guard] skipped removeChild of a foreign node (extension DOM mutation?)');
      return child;
    }
    return originalRemoveChild.call(this, child, ...rest);
  };
  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBeforeGuard(newNode, referenceNode, ...rest) {
    if (referenceNode && referenceNode.parentNode !== this) {
      // eslint-disable-next-line no-console
      console.warn('[cc-dom-guard] skipped insertBefore against a foreign reference node (extension DOM mutation?)');
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode, ...rest);
  };
}

// Initialize viewport height fix for mobile
initViewportHeightFix();

// First-party frontend error reporting (CEL-1117): uncaught errors + unhandled
// rejections + React crashes → POST /api/cellcog/fe-errors → [FEERR] log lane
// (same Cloud Logging pipeline as APIPERF/DBPERF). Registered this early so
// boot-time errors are captured too.
initFeErrorReporting();

// Signup attribution (CEL-491/CEL-526): store first-touch UTMs + referrer + landing page
// in localStorage on the visitor's first pageview. No-ops on later visits (first touch wins).
captureFirstTouch();
// Blog voice "Start this" (CEL-3260): stash the carried role + tasks across signup and stamp start-this once (blogVoiceCapture.js).
captureBlogVoiceHire();

// Home-screen app update check (Sep 30 2026, PWA item 2): a home-screen app has no refresh button,
// so on return from the background it reloads once if a newer build is live. Browser tabs untouched.
installWebUpdateOnResume();

// chart.js is no longer imported anywhere (its last consumer, the per-chat cost window, was retired
// Oct 2026 for the Usage app); the package can be uninstalled.

// react-snap prerenders the public marketing routes to static HTML at build time
// (postbuild step). That static HTML is what search engines and AI crawlers read
// — that's the entire SEO/GEO win, and it's fully preserved regardless of what we
// do on the client.
//
// For the CLIENT, we deliberately use createRoot (NOT hydrateRoot). React 18's
// hydrateRoot is brittle against react-snap's serialized markup (it produced a
// duplicated, non-interactive DOM here). createRoot cleanly REPLACES the
// prerendered markup with one fresh, fully-interactive render. The cost is a
// negligible first-paint replace of identical content; the benefit is bulletproof
// interactivity. Crawlers never run this — they keep the static HTML.
const rootElement = document.getElementById('root');

// Prerender takeover marker (visible double-render fix, Aug 4): when the root
// already contains react-snap's static HTML, createRoot REPLACES a page the
// visitor is ALREADY LOOKING AT. Entrance animations must not replay during
// that swap (the static paint played them once) — components consult this flag
// and render their settled state instead (AIEHero, useReveal). Cleared shortly
// after boot so SPA navigations keep their normal entrance animations.
window.__CC_PRERENDER_TAKEOVER = rootElement.hasChildNodes();
if (window.__CC_PRERENDER_TAKEOVER) {
  setTimeout(() => { window.__CC_PRERENDER_TAKEOVER = false; }, 4000);
}

const app = (
  <React.StrictMode>
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  </React.StrictMode>
);

createRoot(rootElement).render(app);

reportWebVitals();
